import { formatDate, formatTime } from "@/shared/lib/date-time";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { RotateCcwIcon } from "lucide-react";
import type { ScheduleChangeLogDTO } from "../../domain/contracts/sessions";

type ClassSessionChangeLogTableProps = {
  logs: ScheduleChangeLogDTO[];
  isLoading?: boolean;
};

const changeTypeLabels: Record<ScheduleChangeLogDTO["changeType"], string> = {
  time: "ປັບເວລາ",
  room: "ປ່ຽນຫ້ອງ",
  instructor: "ປ່ຽນຄູຜູ້ສອນ",
  capacity: "ປ່ຽນຄວາມຈຸ",
  location: "ສະຖານທີ່ / ຫ້ອງ / ນອກສະຖານທີ່",
  engagement: "ຮູບແບບ (ມາດຕະຖານ / Partner)",
  other: "ອື່ນໆ",
};

export function ClassSessionChangeLogTable({
  logs,
  isLoading,
}: ClassSessionChangeLogTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>ບັນທຶກການປ່ຽນແປງຕາຕະລາງ</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-8 text-center text-muted-foreground">
            ກຳລັງໂຫຼດ...
          </div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            ບໍ່ມີການປ່ຽນແປງ
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ປະເພດການປ່ຽນແປງ</TableHead>
                    <TableHead>ຄ່າເກົ່າ</TableHead>
                    <TableHead>ຄ່າໃໝ່</TableHead>
                    <TableHead>ເຫດຜົນ</TableHead>
                    <TableHead>ຜູ້ປ່ຽນແປງ</TableHead>
                    <TableHead>ເວລາທີ່ປ່ຽນ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <span className="font-semibold">
                          {changeTypeLabels[log.changeType]}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground">
                          {log.oldValue || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-primary">
                          {log.newValue || "-"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{log.reason}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex size-6 items-center justify-center rounded-full bg-muted">
                            <span className="text-muted-foreground text-xs">
                              {log.changedByUserName?.charAt(0).toUpperCase() ||
                                "?"}
                            </span>
                          </div>
                          <span className="text-sm">
                            {log.changedByUserName || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(log.changedAt)}{" "}
                          {formatTime(log.changedAt)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                className="flex items-center gap-2 text-muted-foreground text-sm hover:text-foreground"
                onClick={() => {
                  // TODO: Navigate to full audit trail
                }}
              >
                <RotateCcwIcon className="size-4" />
                ເບິ່ງ Full Audit Trail
              </button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
